import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import CircularProgress from "@mui/material/CircularProgress"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import { useEffect, useState } from "react"

import Column from "../../common/components/Column"
import Row from "../../common/components/Row"
import ActivityReportFormFields from "./activityReportFormFields"

export default function SaveSessionNotesDialog({
  open,
  axios,
  reportDate,
  accountOwnerId = null,
  onSaved,
  onClose,
}) {
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState("")
  const [notes, setNotes] = useState("")
  const [form, setForm] = useState(null)

  useEffect(() => {
    if (!open) {
      return
    }
    setLoading(true)
    setError("")
    const params = { report_date: reportDate }
    if (accountOwnerId) {
      params.account_owner_id = accountOwnerId
    }
    axios
      .get("/clients/api/concierge/daily-activity/report-form/", { params })
      .then(({ data }) => {
        setForm(data?.form || null)
        setNotes(data?.form?.notes || "")
      })
      .catch(() => {
        setForm(null)
        setError("Unable to load session details.")
      })
      .finally(() => setLoading(false))
  }, [axios, open, reportDate, accountOwnerId])

  function handleClose() {
    if (submitting) {
      return
    }
    onClose()
  }

  function handleSubmit() {
    setSubmitting(true)
    setError("")
    const payload = {
      report_date: reportDate,
      notes,
    }
    if (accountOwnerId) {
      payload.account_owner_id = accountOwnerId
    }
    axios
      .put("/clients/api/concierge/daily-activity/notes/", payload)
      .then(({ data }) => onSaved?.(data))
      .catch((requestError) => {
        const detail =
          requestError?.response?.data?.detail ||
          requestError?.response?.data?.notes ||
          "Unable to save notes."
        setError(typeof detail === "string" ? detail : "Unable to save notes.")
      })
      .finally(() => setSubmitting(false))
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: 2000 }}
    >
      <DialogTitle sx={{ pb: 1 }}>Save session notes</DialogTitle>
      <DialogContent>
        <Column gap={2.5} sx={{ pt: 0.5 }}>
          {loading ? (
            <Row sx={{ justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Row>
          ) : form ? (
            <ActivityReportFormFields
              form={form}
              notes={notes}
              onNotesChange={setNotes}
            />
          ) : (
            <Alert severity="error">
              {error || "No activity for this date."}
            </Alert>
          )}
          {error && form ? <Alert severity="error">{error}</Alert> : null}
        </Column>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button variant="outlined" onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={loading || !form || submitting}
          onClick={handleSubmit}
          sx={{ borderRadius: 999, px: 3 }}
        >
          {submitting ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            "Save notes"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
